import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ROAD_WIDTHS, ROAD_COLORS } from '../constants.js';

/**
 * Build a merged road mesh from parsed OSM road data.
 * Uses the offset-polyline (miter-join) technique to expand centrelines into strips.
 *
 * @param {Array}    roads   Parsed road objects
 * @param {Object}   proj    Projection helper
 * @param {Function} [elevFn] Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildRoadMesh(roads, proj, elevFn = null) {
  const roadGeos  = [];
  const markGeos  = [];

  for (const road of roads) {
    // Bridges: wrap elevFn to add vertical clearance above terrain
    const roadElev = road.bridge
      ? _bridgeElevFn(elevFn, road.layer)
      : elevFn;

    const colorHex = ROAD_COLORS[road.highway] ?? ROAD_COLORS.default;
    const geo = buildRoadStrip(road, proj, roadElev, colorHex);
    if (geo) roadGeos.push(geo);

    // Road markings (centre line) for wider, non-bridge roads
    const hw = road.width ? road.width / 2 : (ROAD_WIDTHS[road.highway] ?? ROAD_WIDTHS.default) / 2;
    if (hw >= 5 && !road.bridge) {
      const mGeo = buildCentreLine(road, proj, elevFn);
      if (mGeo) markGeos.push(mGeo);
    }
  }

  const group = new THREE.Group();

  if (roadGeos.length > 0) {
    const merged = mergeGeometries(roadGeos, false);
    roadGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.92,
      metalness: 0.0,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.receiveShadow = true;
    // elevation already baked into vertex Y; tiny global offset prevents z-fighting with terrain
    mesh.position.y = 0.05;
    group.add(mesh);
  }

  if (markGeos.length > 0) {
    const merged = mergeGeometries(markGeos, false);
    markGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.position.y = 0.08;
    group.add(mesh);
  }

  return group;
}

// ── Strip generation ──────────────────────────────────────────────────────────

function buildRoadStrip(road, proj, elevFn, colorHex) {
  const halfW = getHalfWidth(road);
  const pts   = projectAndDeduplicate(road.nodes, proj);
  if (pts.length < 2) return null;

  return buildQuadStrip(pts, halfW, elevFn, colorHex);
}

function buildCentreLine(road, proj, elevFn) {
  const pts = projectAndDeduplicate(road.nodes, proj);
  if (pts.length < 2) return null;
  return buildQuadStrip(pts, 0.15, elevFn); // 0.3 m wide centre dash — no colour attribute
}

// ── Quad strip (miter join) ───────────────────────────────────────────────────

/** Exported for reuse in railways.js and barriers.js */
export function buildQuadStrip(pts, halfW, elevFn, colorHex) {
  const n       = pts.length;
  const leftV   = [];
  const rightV  = [];

  for (let i = 0; i < n; i++) {
    const p = pts[i];
    let nx, nz; // right-side normal * halfW (already scaled)

    if (n === 1) {
      nx = halfW; nz = 0;
    } else if (i === 0) {
      const d  = segDir(pts[0], pts[1]);
      nx = d.nz * halfW;
      nz = -d.nx * halfW;
    } else if (i === n - 1) {
      const d  = segDir(pts[n-2], pts[n-1]);
      nx = d.nz * halfW;
      nz = -d.nx * halfW;
    } else {
      // Miter join
      const d1 = segDir(pts[i-1], pts[i]);
      const d2 = segDir(pts[i],   pts[i+1]);
      const n1x = d1.nz, n1z = -d1.nx;
      const n2x = d2.nz, n2z = -d2.nx;
      const mx  = n1x + n2x;
      const mz  = n1z + n2z;
      const mLen = Math.sqrt(mx*mx + mz*mz);

      if (mLen < 1e-4) {
        // Near-180° turn — fall back to segment normal
        nx = n1x * halfW; nz = n1z * halfW;
      } else {
        const mn = { x: mx / mLen, z: mz / mLen };
        const dot = mn.x * n1x + mn.z * n1z;
        // Cap miter to avoid extreme spikes at sharp corners
        const scale = dot > 0.2 ? halfW / dot : halfW / 0.2;
        nx = mn.x * scale;
        nz = mn.z * scale;
      }
    }

    leftV.push( { x: p.x - nx, z: p.z - nz });
    rightV.push({ x: p.x + nx, z: p.z + nz });
  }

  // Build BufferGeometry quad strip
  const positions = new Float32Array(n * 2 * 3);
  const indices   = [];

  for (let i = 0; i < n; i++) {
    const L = leftV[i], R = rightV[i];
    // Elevation sampled at the road centreline node (same for both edges)
    const y = elevFn ? elevFn(pts[i].x, pts[i].z) : 0;
    positions[i*6 + 0] = L.x; positions[i*6 + 1] = y; positions[i*6 + 2] = L.z;
    positions[i*6 + 3] = R.x; positions[i*6 + 4] = y; positions[i*6 + 5] = R.z;
  }

  for (let i = 0; i < n - 1; i++) {
    const tl = i*2, tr = i*2+1, bl = i*2+2, br = i*2+3;
    indices.push(tl, bl, tr,  tr, bl, br);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();

  if (colorHex !== undefined) {
    const c      = new THREE.Color(colorHex);
    const colors = new Float32Array(n * 2 * 3);
    for (let i = 0; i < n * 2; i++) {
      colors[i * 3]     = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }

  return geo;
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function getHalfWidth(road) {
  if (road.width && isFinite(road.width)) return road.width / 2;
  return (ROAD_WIDTHS[road.highway] ?? ROAD_WIDTHS.default) / 2;
}

/** Exported for reuse in railways.js, barriers.js */
export function projectAndDeduplicate(nodes, proj) {
  const result = [];
  for (const n of nodes) {
    if (n.lat === undefined || n.lon === undefined) continue;
    const p = proj.project(n.lat, n.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    if (result.length > 0) {
      const prev = result[result.length - 1];
      const dx = p.x - prev.x, dz = p.z - prev.z;
      if (dx*dx + dz*dz < 0.0001) continue; // skip duplicate / coincident
    }
    result.push(p);
  }
  return result;
}

/** Unit direction vector from a to b. Exported for reuse. */
export function segDir(a, b) {
  const dx = b.x - a.x, dz = b.z - a.z;
  const len = Math.sqrt(dx*dx + dz*dz) || 1;
  return { nx: dx/len, nz: dz/len };
}

/** Wrap an elevFn to add bridge vertical clearance. */
function _bridgeElevFn(elevFn, layer) {
  const lift = 6 + Math.max(0, (layer - 1)) * 4; // 6m base + 4m per extra layer
  return (x, z) => (elevFn ? elevFn(x, z) : 0) + lift;
}
